import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc } from 'firebase/firestore';
import {
    Search, Users, UserPlus, Menu, RefreshCw, Copy, MessageCircle, Globe,
    Facebook, ChevronRight, ChevronLeft, X,
} from 'lucide-react';
import { auth, db } from './firebase';
import DashboardNav from './DashboardNav';
import {
    CRM_CONFIGS, getInfo, STATUS_OPTIONS, DEFAULT_STATUS, StatusDropdown, Toast, useToast,
    useResellers, addReseller, copyToClipboard, whatsAppLink, getWebsiteLink, initials, formatDate,
} from './resellerCrmShared';

// ─── Add Reseller Modal ───────────────────────────────────────────────────────
const EMPTY_FORM = {
    name: '', company_name: '', phone: '', email: '',
    username: '', address: '', facebook_link: '', website_link: '',
};

const Field = ({ label, required, ...props }) => (
    <label className="block">
        <span className="mb-1 block text-sm font-medium text-gray-700">
            {label} {required && <span className="text-red-500">*</span>}
        </span>
        <input {...props}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-orange-400" />
    </label>
);

const AddResellerModal = ({ open, onClose, onCreated, showToast, cfg }) => {
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    if (!open) return null;

    const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const handleClose = () => { setForm(EMPTY_FORM); onClose(); };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.phone.trim()) return showToast('Name and phone are required', 'error');
        setSaving(true);
        try {
            const newId = await addReseller(form, cfg);
            showToast(`${cfg.singular} added`);
            setForm(EMPTY_FORM);
            onCreated(newId);
        } catch (err) {
            console.error('addReseller failed:', err?.code, err?.message);
            showToast(`Could not add ${cfg.singular.toLowerCase()}`, 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
            <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
                <div className="mb-4 flex items-center justify-between">
                    <h2 className="text-lg font-bold text-gray-900">Add {cfg.singular}</h2>
                    <button type="button" onClick={handleClose} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100">
                        <X size={18} />
                    </button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-3">
                    <Field label="Name" required value={form.name} onChange={update('name')} placeholder="Md Sohibur Rahman" />
                    <Field label="Phone" required value={form.phone} onChange={update('phone')} placeholder="01XXXXXXXXX" />
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Company name" value={form.company_name} onChange={update('company_name')} placeholder="Tee Void Fashion" />
                        <Field label="Email" type="email" value={form.email} onChange={update('email')} placeholder="name@email.com" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Username" value={form.username} onChange={update('username')} placeholder="dropshipping15342" />
                        <Field label="Address" value={form.address} onChange={update('address')} placeholder="City, area" />
                    </div>
                    <Field label="Facebook page link" value={form.facebook_link} onChange={update('facebook_link')} placeholder="https://facebook.com/..." />
                    <Field label="Website link" value={form.website_link} onChange={update('website_link')}
                        placeholder="Leave empty to auto-use mohasagor.com.bd/shop/{username}" />
                    <div className="flex justify-end gap-2 pt-2">
                        <button type="button" onClick={handleClose}
                            className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50">
                            Cancel
                        </button>
                        <button type="submit" disabled={saving}
                            className="rounded-lg bg-orange-400 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-500 disabled:opacity-60">
                            {saving ? 'Saving…' : `Add ${cfg.singular}`}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// ─── Reseller row card ────────────────────────────────────────────────────────
const iconBtn = 'rounded-lg border border-gray-200 p-2 text-gray-500 transition';

const ResellerCard = ({ reseller, onStatusChange, onCopy, onOpen, cfg }) => {
    const info = getInfo(cfg, reseller);
    const pkg = reseller.get_package || {};
    const phone = info.phone || '';
    const website = getWebsiteLink(info);

    return (
        <div className="group flex flex-wrap items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 transition hover:border-orange-300">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-orange-400 text-sm font-semibold text-white">
                {initials(info.name) || '—'}
            </div>

            <button type="button" onClick={() => onOpen(reseller.id)} className="min-w-0 flex-1 text-left">
                <p className="truncate font-semibold text-gray-900">{info.name || 'Unnamed'}</p>
                <p className="truncate text-sm text-gray-500">{info.company_name || '—'} · {phone || 'No phone'}</p>
                <p className="mt-0.5 text-xs text-gray-400">{pkg.name || 'No package'} · ends {formatDate(reseller.end_date)}</p>
            </button>

            <div className="flex shrink-0 items-center gap-2">
                <StatusDropdown status={reseller.crm_status} onChange={(s) => onStatusChange(reseller.id, s)} />
                <button type="button" onClick={() => onCopy(phone)} title="Copy number" className={`${iconBtn} hover:bg-gray-50 hover:text-gray-900`}>
                    <Copy size={16} />
                </button>
                <a href={whatsAppLink(phone)} target="_blank" rel="noreferrer" title="Message on WhatsApp"
                    className={`${iconBtn} hover:bg-emerald-50 hover:text-emerald-600`}>
                    <MessageCircle size={16} />
                </a>
                {website && (
                    <a href={website} target="_blank" rel="noreferrer" title="Open reseller's website"
                        className={`${iconBtn} hover:bg-blue-50 hover:text-blue-600`}>
                        <Globe size={16} />
                    </a>
                )}
                {info.facebook_link && (
                    <a href={info.facebook_link} target="_blank" rel="noreferrer" title="Open Facebook page"
                        className={`${iconBtn} hover:bg-indigo-50 hover:text-indigo-600`}>
                        <Facebook size={16} />
                    </a>
                )}
                <button type="button" onClick={() => onOpen(reseller.id)} className="rounded-lg p-2 text-gray-300 transition group-hover:text-gray-500">
                    <ChevronRight size={18} />
                </button>
            </div>
        </div>
    );
};

// ─── Main page ────────────────────────────────────────────────────────────────
export default function ResellerCrmPage({ config = CRM_CONFIGS.reseller }) {
    const cfg = config;
    const navigate = useNavigate();
    const { toast, showToast } = useToast();
    const [sidebarOpen, setSidebarOpen] = useState(() => typeof window === 'undefined' || window.innerWidth >= 1024);
    const [showAddModal, setShowAddModal] = useState(false);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [localStatus, setLocalStatus] = useState({});

    const { rows, loading, error, hasNext, hasPrev, pageIndex, totalCount, goNext, goPrev, refresh, pageSize } = useResellers(cfg);
    const userEmail = auth.currentUser?.email || '';

    const handleStatusChange = async (resellerId, newStatus) => {
        setLocalStatus((prev) => ({ ...prev, [resellerId]: newStatus }));
        try {
            await updateDoc(doc(db, cfg.collection, String(resellerId)), { crm_status: newStatus });
            showToast('Status updated');
        } catch (err) {
            console.error('updateDoc failed:', err?.code, err?.message);
            showToast('Could not update status', 'error');
            setLocalStatus((prev) => { const c = { ...prev }; delete c[resellerId]; return c; });
        }
    };

    const handleCopy = async (phone) => {
        const ok = await copyToClipboard(phone);
        showToast(ok ? 'Number copied' : 'Could not copy number', ok ? 'success' : 'error');
    };

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        return rows
            .map((r) => ({ ...r, crm_status: localStatus[r.id] ?? r.crm_status ?? DEFAULT_STATUS }))
            .filter((r) => {
                if (statusFilter !== 'all' && r.crm_status !== statusFilter) return false;
                if (!term) return true;
                const info = getInfo(cfg, r);
                return (
                    info.name?.toLowerCase().includes(term) ||
                    info.company_name?.toLowerCase().includes(term) ||
                    info.phone?.includes(term)
                );
            });
    }, [rows, search, statusFilter, localStatus, cfg]);

    const start = pageIndex * pageSize + 1;
    const end = start + pageSize - 1;

    return (
        <div className="flex min-h-screen bg-gray-50 text-gray-900">
            <Toast toast={toast} />
            <DashboardNav sidebarOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

            <div className="flex-1 min-w-0 overflow-auto">
                {/* Header */}
                <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 sticky top-0 z-20">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-orange-50 rounded-lg transition">
                                <Menu size={22} />
                            </button>
                            <div>
                                <h1 className="text-2xl font-black text-gray-900 tracking-tight flex items-center gap-2">
                                    <Users size={22} className="text-orange-600" /> {cfg.title}
                                </h1>
                                <p className="text-xs text-gray-500 mt-0.5">Logged in as: {userEmail}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <button onClick={() => setShowAddModal(true)}
                                className="flex items-center gap-2 bg-orange-400 text-white hover:bg-orange-500 px-4 py-2 rounded-lg text-sm font-semibold transition">
                                <UserPlus size={16} /> Add {cfg.singular}
                            </button>
                            <button onClick={refresh} title="Refresh" className="p-2 hover:bg-orange-50 rounded-lg transition text-gray-500">
                                <RefreshCw size={18} />
                            </button>
                        </div>
                    </div>
                </div>

                <AddResellerModal
                    open={showAddModal}
                    onClose={() => setShowAddModal(false)}
                    onCreated={(id) => { setShowAddModal(false); navigate(`${cfg.detailPath}/${id}`); }}
                    showToast={showToast}
                    cfg={cfg}
                />

                <div className="p-6 max-w-5xl mx-auto">
                    {/* Search + filter */}
                    <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
                        <div className="relative flex-1">
                            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input value={search} onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search by name, company, or phone…"
                                className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm focus:outline-none focus:border-orange-400" />
                        </div>
                        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:border-orange-400">
                            <option value="all">All statuses</option>
                            {Object.entries(STATUS_OPTIONS).map(([key, opt]) => (
                                <option key={key} value={key}>{opt.label}</option>
                            ))}
                        </select>
                    </div>

                    {loading && (
                        <div className="space-y-3">
                            {Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-20 animate-pulse rounded-xl bg-gray-100" />)}
                        </div>
                    )}

                    {error && (
                        <p className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
                            Could not load {cfg.plural}. Please refresh and try again.
                        </p>
                    )}

                    {!loading && !error && filtered.length === 0 && (
                        <div className="rounded-xl border border-dashed border-gray-300 py-16 text-center text-gray-400">
                            No {cfg.plural} found for this search.
                        </div>
                    )}

                    {!loading && !error && filtered.length > 0 && (
                        <div className="space-y-3">
                            {filtered.map((r) => (
                                <ResellerCard key={r.id} reseller={r}
                                    onStatusChange={handleStatusChange}
                                    onCopy={handleCopy}
                                    onOpen={(id) => navigate(`${cfg.detailPath}/${id}`)}
                                    cfg={cfg} />
                            ))}
                        </div>
                    )}

                    {!loading && !error && rows.length > 0 && (
                        <div className="mt-4 flex items-center justify-between border-t border-gray-200 px-1 py-4 text-sm text-gray-500">
                            <p>
                                Showing {start}–{Math.min(end, totalCount ?? end)}
                                {totalCount != null && <> of {totalCount}</>}
                            </p>
                            <div className="flex items-center gap-2">
                                <button type="button" onClick={goPrev} disabled={!hasPrev}
                                    className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-1.5 font-medium text-gray-600 transition hover:bg-orange-50 disabled:cursor-not-allowed disabled:opacity-40">
                                    <ChevronLeft size={16} /> Previous
                                </button>
                                <button type="button" onClick={goNext} disabled={!hasNext}
                                    className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-1.5 font-medium text-gray-600 transition hover:bg-orange-50 disabled:cursor-not-allowed disabled:opacity-40">
                                    Next <ChevronRight size={16} />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
